import { useEffect, useMemo, useRef, useState, KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";

export interface SearchableSelectOption {
  value: string;
  label: string;
}

interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder: string;
  emptyOptionLabel?: string;
  disabled?: boolean;
  /** Max items rendered in the dropdown body (search still filters the full set). */
  maxItems?: number;
}

/**
 * Searchable single-select. Mirrors the visual language of the native <select>
 * we replaced (green focus ring, gray-300 border, rounded-lg) but the body is
 * a custom popover with a filter input, so it scales to thousands of options.
 *
 * Outside-click + Escape close; Arrow keys navigate; Enter selects.
 */
const SearchableSelect = ({
  options,
  value,
  onChange,
  placeholder,
  emptyOptionLabel,
  disabled = false,
  maxItems = 100,
}: SearchableSelectProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlighted, setHighlighted] = useState(0);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  const selected = useMemo(() => options.find((o) => o.value === value) ?? null, [options, value]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options.slice(0, maxItems);
    return options.filter((o) => o.label.toLowerCase().includes(q)).slice(0, maxItems);
  }, [options, query, maxItems]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    if (open) {
      setQuery("");
      setHighlighted(0);
      // Autofocus the search input on open.
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  useEffect(() => {
    if (highlighted < 0 || !listRef.current) return;
    const el = listRef.current.children[highlighted] as HTMLElement | undefined;
    el?.scrollIntoView({ block: "nearest" });
  }, [highlighted]);

  const commit = (idx: number) => {
    if (emptyOptionLabel && idx === 0) {
      onChange(null);
      setOpen(false);
      return;
    }
    const realIdx = emptyOptionLabel ? idx - 1 : idx;
    const item = filtered[realIdx];
    if (!item) return;
    onChange(item.value);
    setOpen(false);
  };

  const handleKey = (e: KeyboardEvent<HTMLInputElement>) => {
    const total = filtered.length + (emptyOptionLabel ? 1 : 0);
    if (total === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlighted((p) => Math.min(p + 1, total - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlighted((p) => Math.max(p - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      commit(highlighted);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const displayLabel = selected?.label ?? placeholder;

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        className={`w-full flex items-center justify-between gap-2 px-3 py-2 border rounded-lg text-sm text-left transition ${
          open ? "border-green-500 ring-2 ring-green-500" : "border-gray-300 hover:border-gray-400"
        } ${disabled ? "opacity-50 cursor-not-allowed bg-gray-50" : "bg-white"}`}
      >
        <span className={`truncate ${selected ? "text-gray-900" : "text-gray-400"}`}>
          {displayLabel}
        </span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform ${
            open ? "rotate-180" : ""
          }`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute top-full left-0 right-0 mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setHighlighted(0);
                }}
                onKeyDown={handleKey}
                placeholder={t("general.search")}
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>
          <div ref={listRef} className="max-h-72 overflow-y-auto py-1">
            {emptyOptionLabel && (
              <button
                type="button"
                onMouseEnter={() => setHighlighted(0)}
                onClick={() => commit(0)}
                className={`block w-full text-left px-3 py-2 text-sm italic text-gray-500 ${
                  highlighted === 0 ? "bg-green-50" : "hover:bg-gray-50"
                }`}
              >
                {emptyOptionLabel}
              </button>
            )}
            {filtered.length === 0 && !emptyOptionLabel ? (
              <div className="px-3 py-4 text-sm text-gray-400 text-center">
                {t("general.noResults", { defaultValue: "Aucun résultat" })}
              </div>
            ) : (
              filtered.map((opt, i) => {
                const idx = emptyOptionLabel ? i + 1 : i;
                const isActive = idx === highlighted;
                const isSelected = opt.value === value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onMouseEnter={() => setHighlighted(idx)}
                    onClick={() => commit(idx)}
                    className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 ${
                      isActive ? "bg-green-50" : "hover:bg-gray-50"
                    } ${isSelected ? "font-medium text-green-700" : "text-gray-700"}`}
                  >
                    <span className="truncate">{opt.label}</span>
                    {isSelected && (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4 text-green-600 flex-shrink-0"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
